import React from "react";
import { Loader2 } from "lucide-react";
import { FaPercent } from "react-icons/fa";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../../../shadcn/ui/dialog";
import { Button } from "../../../../../shadcn/ui/button";
import { Input } from "../../../../../shadcn/ui/input";
import { Label } from "../../../../../shadcn/ui/label";
import { PhoneInput } from "../../../../../components/ui/phone-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../shadcn/ui/select";

const EditOrganizationModal = ({
  isOpen,
  onClose,
  selectedOrganization,
  editOrganizationForm,
  editOrganizationLoading,
  editOrganizationError,
  fetchingOrganizationData,
  onInputChange,
  onSubmit,
  // Host-specific props
  isHostMode = false,
  commissionPercentage = 0,
  onCommissionChange,
  defaultCommission = 0,
  isCustomRevenueShare = false,
  onDeleteRevenueShare,
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Organization</DialogTitle>
        </DialogHeader>

        {fetchingOrganizationData ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading organization data...</span>
          </div>
        ) : (
          <form
            onSubmit={e => {
              e.preventDefault();
              onSubmit();
            }}
          >
            <div className="space-y-3">
              {editOrganizationError && (
                <div className="p-2 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{editOrganizationError}</p>
                </div>
              )}

              <div className="grid grid-cols-1 gap-2.5">
                <div className="space-y-2">
                  <Label htmlFor="name">Organization Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={editOrganizationForm.name || ""}
                    onChange={onInputChange}
                    required
                    placeholder="Enter organization name"
                  />
                </div>

                {!isHostMode && (
                  <div className="space-y-2">
                    <Label htmlFor="code">Organization Code *</Label>
                    <Input
                      id="code"
                      name="code"
                      value={editOrganizationForm.code || ""}
                      onChange={onInputChange}
                      required
                      placeholder="Enter organization code"
                    />
                  </div>
                )}

                {!isHostMode && (
                  <div className="space-y-2">
                    <Label htmlFor="status">Status *</Label>
                    <Select
                      value={editOrganizationForm.status || ""}
                      onValueChange={value => onInputChange({ target: { name: "status", value } })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACTIVE">Active</SelectItem>
                        <SelectItem value="DISABLED">Disabled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="spaceType">Space Type *</Label>
                  <Select
                    value={editOrganizationForm.spaceType || "LOW"}
                    onValueChange={value => onInputChange({ target: { name: "spaceType", value } })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select space type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOW">Low</SelectItem>
                      <SelectItem value="MEDIUM">Medium</SelectItem>
                      <SelectItem value="HIGH">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactEmail">Contact Email *</Label>
                  <Input
                    id="contactEmail"
                    name="contactEmail"
                    type="email"
                    value={editOrganizationForm.contactEmail || ""}
                    onChange={onInputChange}
                    required
                    placeholder="Enter contact email"
                  />
                </div>

                {!isHostMode && (
                  <div className="space-y-2">
                    <Label htmlFor="alternateEmail">Alternate Email</Label>
                    <Input
                      id="alternateEmail"
                      name="alternateEmail"
                      type="email"
                      value={editOrganizationForm.alternateEmail || ""}
                      onChange={onInputChange}
                      placeholder="Enter alternate email"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="contactMsisdn">Contact Phone</Label>
                  <PhoneInput
                    value={editOrganizationForm.contactMsisdn || ""}
                    onChange={val => onInputChange({ target: { name: "contactMsisdn", value: val } })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    name="address"
                    value={editOrganizationForm.address || ""}
                    onChange={onInputChange}
                    placeholder="Enter address"
                  />
                </div>

                {!isHostMode && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        name="city"
                        value={editOrganizationForm.city || ""}
                        onChange={onInputChange}
                        placeholder="Enter city"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="region">Region</Label>
                      <Input
                        id="region"
                        name="region"
                        value={editOrganizationForm.region || ""}
                        onChange={onInputChange}
                        placeholder="Enter region"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="webUrl">Website URL</Label>
                      <Input
                        id="webUrl"
                        name="webUrl"
                        type="url"
                        value={editOrganizationForm.webUrl || ""}
                        onChange={onInputChange}
                        placeholder="Enter website URL"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="domain">Domain</Label>
                      <Input
                        id="domain"
                        name="domain"
                        value={editOrganizationForm.domain || ""}
                        onChange={onInputChange}
                        placeholder="Enter domain"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="logoUrl">Logo URL</Label>
                      <Input
                        id="logoUrl"
                        name="logoUrl"
                        type="url"
                        value={editOrganizationForm.logoUrl || ""}
                        onChange={onInputChange}
                        placeholder="Enter logo URL"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Revenue Share Section for Host Mode */}
              {isHostMode && onCommissionChange && (
                <div className="border-t pt-2 mt-1">
                  <div className="space-y-2">
                    <Label htmlFor="revenue-share-percentage" className="flex items-center gap-2">
                      <FaPercent className="h-4 w-4" />
                      Revenue Share Commission (%)
                    </Label>
                    <Input
                      id="revenue-share-percentage"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={commissionPercentage}
                      onChange={e => onCommissionChange(e.target.value)}
                      placeholder="Enter commission percentage"
                    />
                    {defaultCommission > 0 && (
                      <p className="text-sm text-muted-foreground">Default: {defaultCommission}%</p>
                    )}
                  </div>

                  {isCustomRevenueShare && onDeleteRevenueShare && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md mt-3">
                      <p className="text-sm text-blue-800">
                        This organization has a custom revenue share. Click "Reset to Default" to use the app-wide
                        default.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={onDeleteRevenueShare}
                        className="text-red-600 hover:text-red-700 mt-2"
                      >
                        Reset to Default
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="mt-3">
              <Button type="button" variant="outline" onClick={onClose} disabled={editOrganizationLoading}>
                Cancel
              </Button>
              <Button type="submit" disabled={editOrganizationLoading}>
                {editOrganizationLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editOrganizationLoading ? "Updating..." : "Update Organization"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditOrganizationModal;
